import React, { useEffect, useState } from 'react';
import orheartImage from '../../orheart.webp';
import friendsImage from '../../friends.webp';
import { auraRgbToStyleVars, parseAuraRgb } from '../../utils/auraColor';

const LOOKING_FOR_ORDER = ['male', 'female', 'nb'];
const LOOKING_FOR_LABEL = { male: 'M', female: 'F', nb: 'NB' };

/** Maps saved-poster `lookingForGenders` (male | female | nb) to display e.g. `M, F, NB`. */
export function formatLookingForGendersShort(lookingForGenders) {
  if (!Array.isArray(lookingForGenders) || lookingForGenders.length === 0) return null;
  const normalized = lookingForGenders
    .map((g) => (typeof g === 'string' ? g.trim().toLowerCase() : ''))
    .filter((g) => g && LOOKING_FOR_LABEL[g]);
  const uniq = [...new Set(normalized)];
  if (!uniq.length) return null;
  uniq.sort((a, b) => LOOKING_FOR_ORDER.indexOf(a) - LOOKING_FOR_ORDER.indexOf(b));
  return uniq.map((g) => LOOKING_FOR_LABEL[g]).join(', ');
}

/**
 * Resolves `lookingForGenders` for a feed ad (local saved poster can override the ad when same user).
 * Used by `FeedNicknameTile` and feed tile detail; mirrors HomePage’s prior `feedTileDetailLookingForGenders`.
 */
export function lookingForGendersForFeedAd(ad, savedPosters, currentUserId) {
  if (!ad) return undefined;
  const localPoster =
    currentUserId && ad.userId === currentUserId
      ? (Array.isArray(savedPosters) ? savedPosters : []).find((p) => p.feedAdId === ad.id || p.id === ad.id) ?? null
      : null;
  const fromLocal =
    Array.isArray(localPoster?.lookingForGenders) && localPoster.lookingForGenders.length
      ? localPoster.lookingForGenders
      : null;
  const fromAd = Array.isArray(ad.lookingForGenders) && ad.lookingForGenders.length ? ad.lookingForGenders : null;
  if (formatLookingForGendersShort(fromLocal)) return fromLocal;
  if (formatLookingForGendersShort(fromAd)) return fromAd;
  return undefined;
}

function NicknameVibeAuraBall({ style, className = '' }) {
  if (!style) return null;
  return (
    <span className={`feed-nickname-aura${className ? ` ${className}` : ''}`} style={style} aria-hidden="true">
      <span className="hero-aura-cloud__blob hero-aura-cloud__blob--one" />
      <span className="hero-aura-cloud__blob hero-aura-cloud__blob--two" />
      <span className="hero-aura-cloud__blob hero-aura-cloud__blob--three" />
    </span>
  );
}

/**
 * FIND Luv legend for collage detail popups.
 * @param {{ friend?: boolean, romantic?: boolean, lookingForGenders?: string[], auraColor?: { r: number, g: number, b: number } | null, vibeLabel?: string, vibeStatus?: string | null }} props
 */
export function CollageDetailsLuvLegend({
  friend = false,
  romantic = false,
  lookingForGenders,
  auraColor,
  vibeLabel = 'vibe',
  vibeStatus = null,
}) {
  const showFriend = !!friend;
  const showRomantic = !!romantic;
  const auraRgb = parseAuraRgb(auraColor);
  const auraStyle = auraRgb ? auraRgbToStyleVars(auraRgb) : null;
  const showVibe = !!auraStyle || !!vibeStatus;
  const recomputing = typeof vibeStatus === 'string' && /^recomputing\.*$/i.test(vibeStatus.trim());
  const [recomputeDots, setRecomputeDots] = useState(1);

  useEffect(() => {
    if (!recomputing) {
      setRecomputeDots(1);
      return undefined;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setRecomputeDots(3);
      return undefined;
    }
    const id = window.setInterval(() => {
      setRecomputeDots((n) => (n % 3) + 1);
    }, 400);
    return () => window.clearInterval(id);
  }, [recomputing]);
  const romanticSuffix = formatLookingForGendersShort(lookingForGenders);
  const romanticText = romanticSuffix
    ? `looking for romance with ${romanticSuffix}`
    : 'looking for romance';
  const romanticAria = romanticSuffix
    ? `Heart icon: looking for romance with ${romanticSuffix}`
    : 'Looking for romance';

  if (!showFriend && !showRomantic && !showVibe) return null;

  const ariaLabel = [
    showRomantic ? romanticAria : null,
    showFriend ? 'Friends icon' : null,
    showVibe ? (recomputing ? 'recomputing' : vibeStatus || 'Vibe aura') : null,
  ].filter(Boolean).join('. ') || 'Icons on collages';

  return (
    <div className="earn-foxes-luv-legend feed-tile-detail-luv-legend" aria-label={ariaLabel}>
      {showRomantic ? (
        <div className="earn-foxes-luv-legend-item">
          <img src={orheartImage} alt="" className="earn-foxes-luv-legend-icon" aria-hidden />
          <span>{romanticText}</span>
        </div>
      ) : null}
      {showFriend ? (
        <div className="earn-foxes-luv-legend-item">
          <img
            src={friendsImage}
            alt=""
            className="earn-foxes-luv-legend-icon earn-foxes-luv-legend-icon--friends"
            aria-hidden
          />
          <span>looking for friends</span>
        </div>
      ) : null}
      {showVibe ? (
        <div className="earn-foxes-luv-legend-item">
          {vibeStatus ? (
            <span className="feed-tile-detail-luv-legend-vibe-status">
              {recomputing ? (
                <>
                  recomputing
                  <span className="luv-legend-recomputing-dots" aria-hidden="true">
                    {'.'.repeat(recomputeDots)}
                  </span>
                </>
              ) : vibeStatus}
            </span>
          ) : (
            <NicknameVibeAuraBall style={auraStyle} className="feed-nickname-aura--legend" />
          )}
          <span>{vibeLabel}</span>
        </div>
      ) : null}
    </div>
  );
}
